import { MouseEventHandler, useCallback } from 'react'

import { Item } from '@inno-sdks/files'
import { ContextMenuOptionColors } from '@inno-ui/context-menu'
import {
  ArrowDownRectangleLineIcon,
  ArrowUpRightIcon,
  CursorSquareOnSquareLineIcon,
  FolderArrowDownLineIcon,
  FolderArrowUpLineIcon,
  FolderLineIcon,
  MagnifyerLineIcon,
  PencilLineIcon,
  PrinterLineIcon,
  TrashLineIcon
} from '@inno-ui/icons'
import { toast } from '@inno-ui/toast'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { updateBaseLayout } from 'modules/desktop/pages/MainPage/components/DesktopGrid/utils/reflowItemsForGridResize'
import {
  getShortcutGroupCollisionItems,
  getShortcutGroupInsertionPlan
} from 'modules/desktop/pages/MainPage/components/DesktopGrid/utils/shortcutGroupBounds'
import {
  useDesktopStore,
  useSelectionStore
} from 'modules/desktop/pages/MainPage/stores'
import { useShortcutGroupStore } from 'modules/desktop/pages/MainPage/stores/shortcutGroupStore'
import { useTouchContextMenu } from 'modules/desktop/pages/MainPage/utils/useTouchContextMenu'
import { getShortcutGroupCapabilities } from 'modules/desktop/shortcutGroups'
import { useTranslation } from 'react-i18next'
import {
  BroadcastChannels,
  DesktopBroadcastMessages
} from 'shared/constants/broadcastChannel'
import { ShortcutGridItem } from 'shared/types/desktop-grid'
import { postMessage } from 'shared/utils/broadcastChannel'

import { useTrashItemsMutation } from '../../../api/hooks/useTrashItems'
import {
  useDownloadFile,
  useItemVisibility,
  useOpenItem,
  usePrintFile
} from '../../../hooks'
import { useDownloadAsZip } from '../../../hooks/download/useDownloadAsZip'
import { getItemLink } from '../../../utils'

// fileType holds a mime type for real files; these markers are everything else
// (see getFileType in useFilesListSync) and can't be downloaded.
const NON_DOWNLOADABLE_FILE_TYPES = ['folder', 'shortcut', 'unknown']
const NON_ZIPABLE_FILE_TYPES = ['folder', 'shortcut', 'unknown']

const isDownloadableFileType = (fileType?: string) =>
  !!fileType && !NON_DOWNLOADABLE_FILE_TYPES.includes(fileType)

const isZipableFileType = (fileType?: string) =>
  !!fileType && !NON_ZIPABLE_FILE_TYPES.includes(fileType)

type UseGridItemContextMenuOptions = {
  item: Item
  itemMetaData: ShortcutGridItem
  isGroupContext: boolean
  selectionCount: number
  onRename: () => void
}

/**
 * Builds the right-click menu for a grid item — the single-item actions
 * (preview/download/print/open/rename/trash) and, when several items are
 * selected, the group actions (download all, deselect, trash all). Also owns
 * the touch long-press menu state.
 */
export const useGridItemContextMenu = ({
  item,
  itemMetaData,
  isGroupContext,
  // unused now; trash count comes from trashableSelectionCount below.
  selectionCount: _selectionCount,
  onRename
}: UseGridItemContextMenuOptions) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'desktop.shortcut_item.context_menu'
  })
  const openItem = useOpenItem()
  const trashItemsMutation = useTrashItemsMutation()
  const downloadFile = useDownloadFile()
  const downloadAsZip = useDownloadAsZip()
  const printFile = usePrintFile()
  const clearSelection = useSelectionStore(state => state.clear)
  const shortcutGroups = useShortcutGroupStore(state => state.groups)
  const createShortcutGroup = useShortcutGroupStore(state => state.createGroup)
  const addMembersToShortcutGroup = useShortcutGroupStore(
    state => state.addMembers
  )
  const removeShortcutGroupMembers = useShortcutGroupStore(
    state => state.removeMembers
  )
  const { apps } = useAvailableApps()
  const { shouldShowPreview, shouldShowDownload, shouldShowPrint } =
    useItemVisibility(item)
  const zipableSelectionCount = useSelectionStore(
    state =>
      useDesktopStore
        .getState()
        .items.filter(i => state.selectedIds.has(i.id))
        .filter(i => i.itemType === 'shortcut')
        .filter(i => isZipableFileType(i.fileType)).length
  )
  // widgets have no fileId, so only shortcuts can be trashed.
  const trashableSelectionCount = useSelectionStore(
    state =>
      useDesktopStore
        .getState()
        .items.filter(
          i => state.selectedIds.has(i.id) && i.itemType === 'shortcut'
        ).length
  )
  // Trash is only offered when NONE of the selected items are permanent — if
  // even one is permanent, hide it (mirrors the single-item guard
  // `!item.extra?.isPermanent`).
  const canTrashSelection = useSelectionStore(state => {
    const selected = useDesktopStore
      .getState()
      .items.filter(i => state.selectedIds.has(i.id))
    return (
      selected.length > 0 &&
      selected.every(i => i.itemType !== 'shortcut' || !i.isPermanent)
    )
  })

  const {
    isContextMenuOpen,
    setIsContextMenuOpen,
    closeContextMenu,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel
  } = useTouchContextMenu()

  const handleMoveToTrash = useCallback<MouseEventHandler<HTMLButtonElement>>(
    async e => {
      e.stopPropagation()

      const asyncHandler = async () => {
        await trashItemsMutation.mutateAsync([item.id])

        postMessage(BroadcastChannels.DESKTOP, {
          type: DesktopBroadcastMessages.REMOVE_ITEM,
          payload: { id: itemMetaData.id }
        })

        closeContextMenu()
      }

      toast.info({
        asyncHandler: asyncHandler(),
        asyncStates: {
          loading: { title: t('moving_to_trash') },
          success: { title: t('moved_to_trash') },
          error: { title: t('failed_to_move_to_trash') }
        }
      })
    },
    [t, trashItemsMutation, item.id, itemMetaData.id, closeContextMenu]
  )

  const handlePreview = useCallback<MouseEventHandler<HTMLButtonElement>>(
    e => {
      e.stopPropagation()
      openItem(item)
      closeContextMenu()
    },
    [item, openItem, closeContextMenu]
  )

  const handleDownload = useCallback<MouseEventHandler<HTMLButtonElement>>(
    e => {
      e.stopPropagation()
      downloadFile(item.id)
      closeContextMenu()
    },
    [item.id, downloadFile, closeContextMenu]
  )

  const handlePrint = useCallback<MouseEventHandler<HTMLButtonElement>>(
    e => {
      e.stopPropagation()
      printFile(item.id)
      closeContextMenu()
    },
    [item.id, printFile, closeContextMenu]
  )

  const handleOpenInNewTab = useCallback<MouseEventHandler<HTMLButtonElement>>(
    e => {
      e.stopPropagation()
      const itemLink = getItemLink(item, apps)
      window.open(itemLink, '_blank')?.focus()
      closeContextMenu()
    },
    [item, closeContextMenu, apps]
  )

  const handleRename = useCallback<MouseEventHandler<HTMLButtonElement>>(
    e => {
      e.stopPropagation()
      onRename()
      closeContextMenu()
    },
    [onRename, closeContextMenu]
  )

  const getSelectedDesktopItems = useCallback(() => {
    const { selectedIds } = useSelectionStore.getState()
    return useDesktopStore.getState().items.filter(i => selectedIds.has(i.id))
  }, [])

  // download, zip, and trash only apply to shortcuts; widgets have no fileId.
  const getSelectedShortcutItems = useCallback(
    () => getSelectedDesktopItems().filter(i => i.itemType === 'shortcut'),
    [getSelectedDesktopItems]
  )

  const currentShortcutGroup = shortcutGroups.find(group =>
    group.memberIds.includes(itemMetaData.id)
  )
  const currentGroupCapabilities =
    getShortcutGroupCapabilities(currentShortcutGroup)
  const availableShortcutGroups = shortcutGroups.filter(
    group =>
      group.id !== currentShortcutGroup?.id &&
      getShortcutGroupCapabilities(group).canAcceptMembers
  )

  const handleCreateShortcutGroup = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    e => {
      e.stopPropagation()
      if (itemMetaData.isPinned || currentShortcutGroup) return
      createShortcutGroup(itemMetaData.id)
      closeContextMenu()
    },
    [
      closeContextMenu,
      createShortcutGroup,
      currentShortcutGroup,
      itemMetaData.id,
      itemMetaData.isPinned
    ]
  )

  const handleMoveToShortcutGroup = useCallback(
    (groupId: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      if (itemMetaData.isPinned) return

      const { items: desktopItems, gridSize = { cols: 0, rows: 0 } } =
        useDesktopStore.getState()
      const draggedItem =
        desktopItems.find(candidate => candidate.id === itemMetaData.id) ??
        itemMetaData
      const targetGroup = shortcutGroups.find(group => group.id === groupId)
      if (
        !targetGroup ||
        !getShortcutGroupCapabilities(targetGroup).canAcceptMembers ||
        gridSize.cols <= 0 ||
        gridSize.rows <= 0
      )
        return

      const groupMembers = desktopItems.filter(candidate =>
        targetGroup.memberIds.includes(candidate.id)
      )
      const sourceGroupIds = shortcutGroups
        .filter(group => group.memberIds.includes(draggedItem.id))
        .map(group => group.id)
      // There is no pointer drop position for a context-menu action, so use
      // the same planner as drag-and-drop to choose the first compact,
      // edge-adjacent slot instead of retaining an arbitrary old position.
      const insertionPlan = getShortcutGroupInsertionPlan({
        members: groupMembers,
        item: draggedItem,
        occupied: [
          ...desktopItems.filter(candidate => candidate.id !== draggedItem.id),
          ...getShortcutGroupCollisionItems(shortcutGroups, desktopItems, [
            groupId,
            ...sourceGroupIds
          ])
        ],
        cols: gridSize.cols,
        rows: gridSize.rows
      })
      if (!insertionPlan) return

      const shiftedMemberPositions = new Map(
        insertionPlan.members.map(member => [member.id, member.position])
      )
      const nextItems = desktopItems.map(candidate => {
        const shiftedPosition = shiftedMemberPositions.get(candidate.id)
        if (shiftedPosition) {
          return { ...candidate, position: { ...shiftedPosition } }
        }
        return candidate.id === draggedItem.id
          ? { ...candidate, position: { ...insertionPlan.target } }
          : candidate
      })
      useDesktopStore.getState().setItems(nextItems)
      updateBaseLayout(nextItems, gridSize.cols, gridSize.rows)
      addMembersToShortcutGroup(groupId, [itemMetaData.id])
      closeContextMenu()
    },
    [addMembersToShortcutGroup, closeContextMenu, itemMetaData, shortcutGroups]
  )

  const handleRemoveFromShortcutGroup = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    e => {
      e.stopPropagation()
      if (!currentGroupCapabilities.canRemoveMembers) return
      removeShortcutGroupMembers([itemMetaData.id])
      closeContextMenu()
    },
    [
      closeContextMenu,
      currentGroupCapabilities.canRemoveMembers,
      itemMetaData.id,
      removeShortcutGroupMembers
    ]
  )

  const handleGroupDownload = useCallback<MouseEventHandler<HTMLButtonElement>>(
    e => {
      e.stopPropagation()
      getSelectedShortcutItems()
        .filter(i => isDownloadableFileType(i.fileType))
        .forEach(i => downloadFile(i.fileId))
      closeContextMenu()
    },
    [getSelectedShortcutItems, downloadFile, closeContextMenu]
  )

  const handleGroupDownloadAsZip = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    e => {
      e.stopPropagation()
      const items = getSelectedShortcutItems()
        .filter(i => isZipableFileType(i.fileType))
        .map(i => ({ id: i.fileId, name: i.label }))
      downloadAsZip(items)
      closeContextMenu()
    },
    [getSelectedShortcutItems, downloadAsZip, closeContextMenu]
  )

  const handleGroupTrash = useCallback<MouseEventHandler<HTMLButtonElement>>(
    e => {
      e.stopPropagation()
      const selected = getSelectedShortcutItems()
      const count = selected.length
      if (!count) return

      const asyncHandler = async () => {
        await trashItemsMutation.mutateAsync(selected.map(i => i.fileId))

        selected.forEach(i =>
          postMessage(BroadcastChannels.DESKTOP, {
            type: DesktopBroadcastMessages.REMOVE_ITEM,
            payload: { id: i.id }
          })
        )

        clearSelection()
        closeContextMenu()
      }

      toast.info({
        asyncHandler: asyncHandler(),
        asyncStates: {
          loading: { title: t('group.moving_to_trash', { count }) },
          success: { title: t('group.moved_to_trash', { count }) },
          error: { title: t('group.failed_to_move_to_trash') }
        }
      })
    },
    [
      t,
      getSelectedShortcutItems,
      trashItemsMutation,
      clearSelection,
      closeContextMenu
    ]
  )

  const handleGroupDeselect = useCallback<MouseEventHandler<HTMLButtonElement>>(
    e => {
      e.stopPropagation()
      closeContextMenu()
      setTimeout(clearSelection, 1)
    },
    [clearSelection, closeContextMenu]
  )

  const groupOptions = [
    ...(zipableSelectionCount > 0
      ? [
          {
            icon: ArrowDownRectangleLineIcon,
            label: t('group.download_selected', {
              count: zipableSelectionCount
            }),
            options: [
              {
                icon: ArrowDownRectangleLineIcon,
                label: t('group.download_separately'),
                onClick: handleGroupDownload
              },
              {
                icon: FolderArrowDownLineIcon,
                label: t('group.download_as_zip'),
                onClick: handleGroupDownloadAsZip
              }
            ]
          }
        ]
      : []),
    {
      icon: CursorSquareOnSquareLineIcon,
      label: t('group.deselect_all'),
      onClick: handleGroupDeselect
    },
    ...(trashableSelectionCount > 0 && canTrashSelection
      ? [
          { isSeparator: true },
          {
            icon: TrashLineIcon,
            label: t('group.move_selected_to_trash', {
              count: trashableSelectionCount
            }),
            color: ContextMenuOptionColors.Red,
            onClick: handleGroupTrash
          }
        ]
      : [])
  ]

  const singleOptions = [
    ...(shouldShowPreview
      ? [
          {
            icon: MagnifyerLineIcon,
            label: t('preview'),
            onClick: handlePreview
          }
        ]
      : []),
    ...(shouldShowDownload
      ? [
          {
            icon: ArrowDownRectangleLineIcon,
            label: t('download'),
            onClick: handleDownload
          }
        ]
      : []),
    ...(shouldShowPrint
      ? [
          {
            icon: PrinterLineIcon,
            label: t('print'),
            onClick: handlePrint
          }
        ]
      : []),
    {
      icon: ArrowUpRightIcon,
      label: t('open_in_new_tab'),
      onClick: handleOpenInNewTab
    },
    ...(!itemMetaData.isPinned && !currentShortcutGroup
      ? [
          {
            icon: FolderArrowDownLineIcon,
            label: t('create_group'),
            onClick: handleCreateShortcutGroup
          }
        ]
      : []),
    ...(currentShortcutGroup && currentGroupCapabilities.canRemoveMembers
      ? [
          {
            icon: FolderArrowUpLineIcon,
            label: t('remove_from_group'),
            onClick: handleRemoveFromShortcutGroup
          }
        ]
      : []),
    ...(!itemMetaData.isPinned &&
    currentGroupCapabilities.canMoveMembers &&
    availableShortcutGroups.length > 0
      ? [
          {
            icon: FolderLineIcon,
            label: t('move_to_group'),
            options: availableShortcutGroups.map(group => ({
              label: group.title,
              onClick: handleMoveToShortcutGroup(group.id)
            }))
          }
        ]
      : []),
    ...(!itemMetaData.isPinned
      ? [
          {
            icon: PencilLineIcon,
            label: t('rename'),
            onClick: handleRename
          }
        ]
      : []),
    ...(!item.extra?.isPermanent
      ? [
          { isSeparator: true },
          {
            icon: TrashLineIcon,
            label: t('move_to_trash'),
            color: ContextMenuOptionColors.Red,
            onClick: handleMoveToTrash
          }
        ]
      : [])
  ]

  return {
    isContextMenuOpen,
    setIsContextMenuOpen,
    closeContextMenu,
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchCancel
    },
    options: isGroupContext ? groupOptions : singleOptions
  }
}
